import { DecoratorNode, $applyNodeReplacement } from 'lexical';
import { markRaw } from 'vue';
import EditorMedia from '../Components/EditorMedia.vue';
import { blockRhythm, blockFrame, blockImage, blockFlush, blockCaption } from '../Themes/EditorClasses';

function convertImageElement(domNode) {
    const src = domNode.getAttribute('src');

    if (!src) return null;

    const altText = domNode.getAttribute('alt') || '';
    const width = domNode.getAttribute('data-image-width') || domNode.naturalWidth || null;
    const height = domNode.getAttribute('data-image-height') || domNode.naturalHeight || null;

    return { node: $createImageNode({ src, altText, width, height }) };
}

export class ImageNode extends DecoratorNode {
    __src;
    __altText;
    __width;
    __height;
    __caption;
    __spacing;

    static getType() {
        return 'image';
    }

    static clone(node) {
        return new ImageNode(node.__src, node.__altText, node.__width, node.__height, node.__caption, node.__spacing, node.__key);
    }

    constructor(src, altText, width, height, caption, spacing, key) {
        super(key);
        this.__src = src;
        this.__altText = altText || '';
        this.__width = width || null;
        this.__height = height || null;
        this.__caption = caption || '';
        this.__spacing = spacing !== false;
    }

    static importJSON(serializedNode) {
        const { src, altText, width, height, caption, spacing } = serializedNode;
        return $createImageNode({ src, altText, width, height, caption, spacing });
    }

    exportJSON() {
        return {
            type: 'image',
            version: 1,
            src: this.__src,
            altText: this.__altText,
            width: this.__width,
            height: this.__height,
            caption: this.__caption,
            spacing: this.__spacing,
        };
    }

    static importDOM() {
        return {
            img: () => ({
                conversion: convertImageElement,
                priority: 0,
            }),
        };
    }

    exportDOM() {
        const figure = document.createElement('figure');
        figure.className = `${blockRhythm} ${blockFrame} ${blockImage}`;
        if (!this.__spacing) {
            figure.classList.add(blockFlush);
        }

        const img = document.createElement('img');
        img.setAttribute('src', this.__src);
        img.setAttribute('alt', this.__altText);

        if (this.__width) {
            img.setAttribute('data-image-width', String(this.__width));
        }

        if (this.__height) {
            img.setAttribute('data-image-height', String(this.__height));
        }

        figure.appendChild(img);

        if (this.__caption) {
            const figcaption = document.createElement('figcaption');
            figcaption.className = blockCaption;
            figcaption.textContent = this.__caption;
            figure.appendChild(figcaption);
        }

        return { element: figure };
    }

    isInline() {
        return false;
    }

    createDOM() {
        const div = document.createElement('div');
        return div;
    }

    updateDOM(prevNode) {
        return prevNode.__src !== this.__src
            || prevNode.__altText !== this.__altText
            || prevNode.__caption !== this.__caption;
    }

    decorate(editor) {
        const aspectRatio = this.__width && this.__height
            ? this.__width / this.__height
            : null;

        return {
            component: markRaw(EditorMedia),
            props: {
                type: 'image',
                src: this.__src,
                altText: this.__altText,
                caption: this.__caption,
                spacing: this.__spacing,
                aspectRatio,
                editable: editor.isEditable(),
            },
        };
    }

    getSrc() {
        return this.__src;
    }

    getAltText() {
        return this.__altText;
    }

    getCaption() {
        return this.__caption;
    }

    getSpacing() {
        return this.__spacing;
    }

    setSrc(src) {
        this.getWritable().__src = src;
    }

    setAltText(altText) {
        this.getWritable().__altText = altText || '';
    }

    setCaption(caption) {
        this.getWritable().__caption = caption || '';
    }

    setSpacing(spacing) {
        this.getWritable().__spacing = spacing !== false;
    }

    setWidthAndHeight(width, height) {
        const writable = this.getWritable();
        writable.__width = width;
        writable.__height = height;
    }
}

export function $createImageNode({ src, altText, width, height, caption, spacing }) {
    return $applyNodeReplacement(new ImageNode(src, altText || '', width || null, height || null, caption || '', spacing));
}

export function $isImageNode(node) {
    return node instanceof ImageNode;
}
